import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputTextarea } from "primereact/inputtextarea";
import { ProgressSpinner } from "primereact/progressspinner";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { toast } from "react-toastify";
import { SET_SHIFT_STATUS } from "../../redux/slices/activeShiftSlice";
import { acquireRegister, clockIn, clockOut, fetchShiftStatus, formatHours, getCurrentPosition, logBreak, releaseRegister } from "../../services/shiftService";
import { fetchCashRegisters } from "../../services/cashService";
import DualCurrencyValue from "../../components/money/DualCurrencyValue";
import styles from "./shifts.module.scss";

const ClockInOut = () => {
    const dispatch = useDispatch();
    const { checked, onShift, pendingApproval, shift } = useSelector((s) => s.activeShiftSlice);
    const role = localStorage.getItem("role");
    const isClerk = role === "clerk";
    // Matches the backend's clock-out gate exactly (shift.cashRegister
    // presence, not workLocation/role) - a register can now be acquired
    // mid-shift by any role (admin/handler included, via the acquire button
    // or the lazy in-store-pickup assignment), not just chosen by a clerk at
    // clock-in, so this can never be clerk-only or it'll miss prompting
    // someone the backend is about to require closingCash from.
    const hasRegister = onShift && !!shift?.cashRegister;
    const [initialLoading, setInitialLoading] = useState(!checked);
    const [busy, setBusy] = useState(false);
    const [showBreakDialog, setShowBreakDialog] = useState(false);
    const [breakHours, setBreakHours] = useState(null);
    const [breakNote, setBreakNote] = useState("");
    const [now, setNow] = useState(Date.now());
    const [wantsRegister, setWantsRegister] = useState(false);
    const [startingCashUsd, setStartingCashUsd] = useState(null);
    const [startingCashJmd, setStartingCashJmd] = useState(null);
    const [cashRegisterId, setCashRegisterId] = useState(null);
    const [registers, setRegisters] = useState([]);
    const [acquireRegisterId, setAcquireRegisterId] = useState(null);
    const [acquireStartingCashUsd, setAcquireStartingCashUsd] = useState(null);
    const [acquireStartingCashJmd, setAcquireStartingCashJmd] = useState(null);
    const [registerBusy, setRegisterBusy] = useState(false);
    const [showClosingCashDialog, setShowClosingCashDialog] = useState(false);
    const [closingCashUsd, setClosingCashUsd] = useState(null);
    const [closingCashJmd, setClosingCashJmd] = useState(null);
    const [showReleaseCashDialog, setShowReleaseCashDialog] = useState(false);
    const [releaseClosingCashUsd, setReleaseClosingCashUsd] = useState(null);
    const [releaseClosingCashJmd, setReleaseClosingCashJmd] = useState(null);
    const [closeSummary, setCloseSummary] = useState(null);

    useEffect(() => {
        const refresh = async () => {
            const res = await fetchShiftStatus();
            if (res?.success) dispatch(SET_SHIFT_STATUS(res.data));
            setInitialLoading(false);
        };
        refresh();
    }, [dispatch]);

    // Needed both for the clerk's clock-in picker and for the acquire-a-
    // register action available to anyone on an active shift - reloaded
    // after acquire/release too so occupancy indicators stay current.
    const loadRegisters = async () => {
        const res = await fetchCashRegisters();
        setRegisters(res?.data || []);
    };

    useEffect(() => {
        loadRegisters();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const registerOptions = useMemo(
        () => registers.map((r) => ({ label: r.inUseBy ? `${r.name} - in use by ${r.inUseBy}` : r.name, value: r._id, disabled: !!r.inUseBy })),
        [registers],
    );

    useEffect(() => {
        if (!onShift) return;
        const tick = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(tick);
    }, [onShift]);

    useEffect(() => {
        if (!pendingApproval) return;
        const poll = setInterval(async () => {
            const res = await fetchShiftStatus();
            if (res?.success) dispatch(SET_SHIFT_STATUS(res.data));
        }, 20000);
        return () => clearInterval(poll);
    }, [pendingApproval, dispatch]);

    const elapsed = useMemo(() => {
        if (!onShift || !shift?.clockIn?.time) return "00:00:00";
        const diffMs = Math.max(0, now - new Date(shift.clockIn.time).getTime());
        const totalSeconds = Math.floor(diffMs / 1000);
        const h = String(Math.floor(totalSeconds / 3600)).padStart(2, "0");
        const m = String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, "0");
        const s = String(totalSeconds % 60).padStart(2, "0");
        return `${h}:${m}:${s}`;
    }, [onShift, shift, now]);

    const handleClockIn = async () => {
        if (isClerk && wantsRegister && !cashRegisterId) {
            toast.warn("Select which register you're working before you clock in.");
            return;
        }
        setBusy(true);
        try {
            const position = await getCurrentPosition();
            // workLocation is derived, not asked directly - acquiring a
            // register at clock-in is what "in_store" has always meant to
            // the rest of the app (cash tracking, clock-out reconciliation).
            const extra = isClerk
                ? {
                      workLocation: wantsRegister ? "in_store" : "remote",
                      ...(wantsRegister ? { startingCashUsd: startingCashUsd || 0, startingCashJmd: startingCashJmd || 0, cashRegisterId } : {}),
                  }
                : {};
            const res = await clockIn(dispatch, position.coords.latitude, position.coords.longitude, extra);
            if (res?.success) {
                const isActive = res.data?.status === "active";
                dispatch(SET_SHIFT_STATUS({ onShift: isActive, pendingApproval: !isActive, shift: res.data }));
                if (!isActive) {
                    toast.info(res.message || "This is an extra shift today - waiting for admin approval.");
                }
            }
        } catch (error) {
            toast.warn("We need your location to clock in. Please allow location access and try again.");
        } finally {
            setBusy(false);
        }
    };

    const performClockOut = async (extra = {}) => {
        setBusy(true);
        try {
            const position = await getCurrentPosition();
            const res = await clockOut(dispatch, position.coords.latitude, position.coords.longitude, extra);
            if (res?.success) {
                dispatch(SET_SHIFT_STATUS({ onShift: false, pendingApproval: false, shift: null }));
                toast.success(`Clocked out. You worked ${formatHours(res.data?.totalHours)} today.`);
                if (res.data?.expectedClosingCash !== undefined || res.data?.cashDiscrepancy !== undefined) {
                    setCloseSummary({
                        expectedClosingCash: res.data?.expectedClosingCash,
                        closingCash: res.data?.closingCash,
                        cashDiscrepancy: res.data?.cashDiscrepancy,
                    });
                }
            }
        } catch (error) {
            toast.warn("We need your location to clock out. Please allow location access and try again.");
        } finally {
            setBusy(false);
        }
    };

    const handleClockOut = () => {
        if (hasRegister) {
            setClosingCashUsd(null);
            setClosingCashJmd(null);
            setShowClosingCashDialog(true);
            return;
        }
        confirmDialog({
            message: "Clock out now? This ends your current shift.",
            header: "Clock Out",
            icon: "pi pi-exclamation-triangle",
            acceptClassName: "p-button-danger",
            accept: () => performClockOut({}),
        });
    };

    const handleConfirmClosingCash = () => {
        setShowClosingCashDialog(false);
        performClockOut({ closingCashUsd: closingCashUsd || 0, closingCashJmd: closingCashJmd || 0 });
    };

    const handleLogBreak = async () => {
        if (!breakHours || breakHours <= 0) {
            toast.warn("Enter how many hours your break was.");
            return;
        }
        setBusy(true);
        try {
            const res = await logBreak(dispatch, breakHours, breakNote);
            if (res?.success) {
                dispatch(SET_SHIFT_STATUS({ onShift: true, shift: res.data }));
                setShowBreakDialog(false);
                setBreakHours(null);
                setBreakNote("");
            }
        } finally {
            setBusy(false);
        }
    };

    const handleAcquireRegister = async () => {
        if (!acquireRegisterId) {
            toast.warn("Select a register to acquire.");
            return;
        }
        setRegisterBusy(true);
        try {
            const res = await acquireRegister(dispatch, acquireRegisterId, acquireStartingCashUsd || 0, acquireStartingCashJmd || 0);
            if (res?.success) {
                dispatch(SET_SHIFT_STATUS({ onShift: true, shift: res.data }));
                setAcquireRegisterId(null);
                setAcquireStartingCashUsd(null);
                setAcquireStartingCashJmd(null);
                toast.success("Register acquired");
                loadRegisters();
            }
        } finally {
            setRegisterBusy(false);
        }
    };

    const handleReleaseRegister = () => {
        setReleaseClosingCashUsd(null);
        setReleaseClosingCashJmd(null);
        setShowReleaseCashDialog(true);
    };

    const handleConfirmReleaseCash = async () => {
        setRegisterBusy(true);
        try {
            const res = await releaseRegister(dispatch, releaseClosingCashUsd || 0, releaseClosingCashJmd || 0);
            if (res?.success) {
                dispatch(SET_SHIFT_STATUS({ onShift: true, shift: res.data.shift }));
                setShowReleaseCashDialog(false);
                toast.success("Register released");
                setCloseSummary({
                    expectedClosingCash: res.data.expectedClosingCash,
                    closingCash: res.data.closingCash,
                    cashDiscrepancy: res.data.discrepancy,
                });
                loadRegisters();
            }
        } finally {
            setRegisterBusy(false);
        }
    };

    if (initialLoading) {
        return (
            <div className={styles.page}>
                <div className={styles.loadingState}>
                    <ProgressSpinner strokeWidth="4" />
                    <span>Checking your shift status...</span>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.page}>
            <ConfirmDialog />

            <div className={styles.pageHeader}>
                <div className={styles.headerTitleRow}>
                    <div className={styles.headerIcon}>
                        <i className="pi pi-clock" />
                    </div>
                    <div>
                        <h2>Shift</h2>
                        <p className={styles.subtitle}>Clock in to start your shift and get access to the system</p>
                    </div>
                </div>
            </div>

            <div className={`${styles.shiftCard} ${onShift ? styles.onShift : ""} ${pendingApproval ? styles.pending : ""}`}>
                <div className={`${styles.statusRing} ${onShift ? styles.onShift : ""} ${pendingApproval ? styles.pending : ""}`}>
                    <i className={onShift ? "pi pi-check" : pendingApproval ? "pi pi-hourglass" : "pi pi-clock"} />
                </div>

                {onShift ? (
                    <>
                        <h3 className={styles.shiftHeading}>You're on shift</h3>
                        <div className={styles.elapsedTime}>{elapsed}</div>
                        <p className={styles.shiftSub}>Your location is recorded at random times while you're on shift.</p>

                        <div className={styles.metaGrid}>
                            <div className={styles.metaBox}>
                                <div className={styles.metaLabel}>Clocked In</div>
                                <div className={styles.metaValue}>{shift?.clockIn?.time ? new Date(shift.clockIn.time).toLocaleTimeString() : "-"}</div>
                            </div>
                            <div className={styles.metaBox}>
                                <div className={styles.metaLabel}>Breaks Logged</div>
                                <div className={styles.metaValue}>{shift?.breaks?.length || 0}</div>
                            </div>
                            {isClerk && (
                                <div className={styles.metaBox}>
                                    <div className={styles.metaLabel}>Work Location</div>
                                    <div className={styles.metaValue}>{shift?.workLocation === "in_store" ? "In Store" : "Remote"}</div>
                                </div>
                            )}
                            {hasRegister && (
                                <div className={styles.metaBox}>
                                    <div className={styles.metaLabel}>Starting Cash</div>
                                    <DualCurrencyValue pair={shift?.startingCash} compact />
                                </div>
                            )}
                            <div className={styles.metaBox}>
                                <div className={styles.metaLabel}>Register</div>
                                <div className={styles.metaValue}>{shift?.cashRegister?.name || "None"}</div>
                            </div>
                        </div>

                        <div className={styles.actionRow} style={{ marginBottom: "10px" }}>
                            {shift?.cashRegister ? (
                                <Button
                                    label={registerBusy ? "Releasing..." : "Release Register"}
                                    icon={registerBusy ? "pi pi-spin pi-spinner" : "pi pi-sign-out"}
                                    className={styles.secondaryButton}
                                    onClick={handleReleaseRegister}
                                    disabled={registerBusy}
                                />
                            ) : (
                                <>
                                    <Dropdown value={acquireRegisterId} options={registerOptions} onChange={(e) => setAcquireRegisterId(e.value)} placeholder="Select a register" style={{ flex: "1 1 220px", minWidth: 0 }} />
                                    <InputNumber
                                        value={acquireStartingCashUsd}
                                        onValueChange={(e) => setAcquireStartingCashUsd(e.value)}
                                        mode="currency"
                                        currency="USD"
                                        locale="en-US"
                                        min={0}
                                        placeholder="Starting cash (USD)"
                                        style={{ flex: "1 1 160px", minWidth: 0 }}
                                    />
                                    <InputNumber
                                        value={acquireStartingCashJmd}
                                        onValueChange={(e) => setAcquireStartingCashJmd(e.value)}
                                        mode="currency"
                                        currency="JMD"
                                        locale="en-US"
                                        min={0}
                                        placeholder="Starting cash (JMD)"
                                        style={{ flex: "1 1 160px", minWidth: 0 }}
                                    />
                                    <Button
                                        label={registerBusy ? "Acquiring..." : "Acquire Register"}
                                        icon={registerBusy ? "pi pi-spin pi-spinner" : "pi pi-sign-in"}
                                        className={styles.secondaryButton}
                                        onClick={handleAcquireRegister}
                                        disabled={registerBusy}
                                    />
                                </>
                            )}
                        </div>

                        <div className={styles.actionRow}>
                            <Button label="Log a Break" icon="pi pi-pause" className={styles.secondaryButton} onClick={() => setShowBreakDialog(true)} disabled={busy} />
                            <Button label={busy ? "Please wait..." : "Clock Out"} icon={busy ? "pi pi-spin pi-spinner" : "pi pi-sign-out"} className={styles.dangerButton} onClick={handleClockOut} disabled={busy} />
                        </div>
                    </>
                ) : pendingApproval ? (
                    <>
                        <h3 className={styles.shiftHeading}>Waiting for admin approval</h3>
                        <p className={styles.shiftSub}>
                            You've already had a shift today, so this extra one needs an admin to approve it before you can start. You'll get access automatically once it's approved - no
                            need to keep refreshing.
                        </p>
                        <div className={styles.metaGrid}>
                            <div className={styles.metaBox}>
                                <div className={styles.metaLabel}>Requested At</div>
                                <div className={styles.metaValue}>{shift?.clockIn?.time ? new Date(shift.clockIn.time).toLocaleTimeString() : "-"}</div>
                            </div>
                        </div>
                    </>
                ) : (
                    <>
                        <h3 className={styles.shiftHeading}>You're not clocked in</h3>
                        <p className={styles.shiftSub}>You need an active shift to use the system. Clock in to record your start time and location.</p>
                        {isClerk && (
                            <>
                                <div className={styles.registerToggleRow}>
                                    <Checkbox inputId="wantsRegister" checked={wantsRegister} onChange={(e) => setWantsRegister(e.checked)} />
                                    <label htmlFor="wantsRegister">Acquire a cash register for this shift</label>
                                </div>
                                {wantsRegister && (
                                    <div className={styles.metaGrid}>
                                        <div className={styles.metaBox}>
                                            <div className={styles.metaLabel}>Starting Cash (USD)</div>
                                            <InputNumber
                                                value={startingCashUsd}
                                                onValueChange={(e) => setStartingCashUsd(e.value)}
                                                mode="currency"
                                                currency="USD"
                                                locale="en-US"
                                                min={0}
                                                placeholder="0.00"
                                                style={{ width: "100%" }}
                                                inputStyle={{ width: "100%" }}
                                            />
                                        </div>
                                        <div className={styles.metaBox}>
                                            <div className={styles.metaLabel}>Starting Cash (JMD)</div>
                                            <InputNumber
                                                value={startingCashJmd}
                                                onValueChange={(e) => setStartingCashJmd(e.value)}
                                                mode="currency"
                                                currency="JMD"
                                                locale="en-US"
                                                min={0}
                                                placeholder="0.00"
                                                style={{ width: "100%" }}
                                                inputStyle={{ width: "100%" }}
                                            />
                                        </div>
                                        <div className={styles.metaBox}>
                                            <div className={styles.metaLabel}>Register</div>
                                            <Dropdown value={cashRegisterId} options={registerOptions} onChange={(e) => setCashRegisterId(e.value)} placeholder="Select a register" style={{ width: "100%" }} />
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                        <div className={styles.actionRow}>
                            <Button label={busy ? "Clocking in..." : "Clock In"} icon={busy ? "pi pi-spin pi-spinner" : "pi pi-sign-in"} className={styles.primaryButton} onClick={handleClockIn} disabled={busy} />
                        </div>
                    </>
                )}
            </div>

            {closeSummary && (
                <div className={styles.sectionCard}>
                    <div className={styles.sectionCardHeader}>
                        <h3>
                            <i className="pi pi-money-bill" />
                            Closing Cash Summary
                        </h3>
                        <Button icon="pi pi-times" className="p-button-text p-button-sm" onClick={() => setCloseSummary(null)} />
                    </div>
                    <div className={styles.metaGrid}>
                        <div className={styles.metaBox}>
                            <div className={styles.metaLabel}>Expected Closing Cash</div>
                            <DualCurrencyValue pair={closeSummary.expectedClosingCash} compact />
                        </div>
                        <div className={styles.metaBox}>
                            <div className={styles.metaLabel}>Counted Closing Cash</div>
                            <DualCurrencyValue pair={closeSummary.closingCash} compact />
                        </div>
                        <div className={styles.metaBox}>
                            <div className={styles.metaLabel}>Discrepancy</div>
                            <DualCurrencyValue
                                pair={closeSummary.cashDiscrepancy}
                                compact
                                danger={Math.abs(closeSummary.cashDiscrepancy?.usd || 0) >= 0.01 || Math.abs(closeSummary.cashDiscrepancy?.jmd || 0) >= 0.01}
                            />
                        </div>
                    </div>
                </div>
            )}

            {onShift && (
                <div className={styles.sectionCard}>
                    <div className={styles.sectionCardHeader}>
                        <h3>
                            <i className="pi pi-pause-circle" />
                            Today's Breaks
                        </h3>
                    </div>
                    {shift?.breaks?.length ? (
                        [...shift.breaks].reverse().map((b, idx) => (
                            <div className={styles.breakRow} key={idx}>
                                <strong>{formatHours(b.hours)}</strong>
                                <span>{b.note || "No note"}</span>
                                <span>{new Date(b.loggedAt).toLocaleTimeString()}</span>
                            </div>
                        ))
                    ) : (
                        <div className={styles.emptyBreaks}>No breaks logged yet today.</div>
                    )}
                </div>
            )}

            <Dialog header="Log a Break" visible={showBreakDialog} style={{ width: "min(420px, 92vw)" }} onHide={() => setShowBreakDialog(false)}>
                <div style={{ display: "flex", flexDirection: "column", gap: "14px", marginTop: "6px" }}>
                    <div>
                        <label style={{ display: "block", fontSize: "12px", fontWeight: 600, color: "#64748b", marginBottom: "6px" }}>Break duration (hours)</label>
                        <InputNumber
                            value={breakHours}
                            onValueChange={(e) => setBreakHours(e.value)}
                            min={0.1}
                            max={12}
                            step={0.25}
                            minFractionDigits={0}
                            maxFractionDigits={2}
                            placeholder="e.g. 0.5"
                            style={{ width: "100%" }}
                            inputStyle={{ width: "100%" }}
                        />
                    </div>
                    <div>
                        <label style={{ display: "block", fontSize: "12px", fontWeight: 600, color: "#64748b", marginBottom: "6px" }}>Note (optional)</label>
                        <InputTextarea value={breakNote} onChange={(e) => setBreakNote(e.target.value)} rows={3} style={{ width: "100%" }} placeholder="Lunch, rest, etc." />
                    </div>
                    <Button label={busy ? "Saving..." : "Save Break"} icon={busy ? "pi pi-spin pi-spinner" : "pi pi-check"} className={styles.primaryButton} onClick={handleLogBreak} disabled={busy} />
                </div>
            </Dialog>

            <Dialog header="Closing Cash" visible={showClosingCashDialog} style={{ width: "min(420px, 92vw)" }} onHide={() => setShowClosingCashDialog(false)}>
                <div style={{ display: "flex", flexDirection: "column", gap: "14px", marginTop: "6px" }}>
                    <p style={{ fontSize: "13px", color: "#64748b", margin: 0 }}>You clocked in with an in-store shift. Count the cash in the register and enter it below before clocking out.</p>
                    <div>
                        <label style={{ display: "block", fontSize: "12px", fontWeight: 600, color: "#64748b", marginBottom: "6px" }}>Closing Cash - USD (counted)</label>
                        <InputNumber
                            value={closingCashUsd}
                            onValueChange={(e) => setClosingCashUsd(e.value)}
                            mode="currency"
                            currency="USD"
                            locale="en-US"
                            min={0}
                            placeholder="0.00"
                            style={{ width: "100%" }}
                            inputStyle={{ width: "100%" }}
                        />
                    </div>
                    <div>
                        <label style={{ display: "block", fontSize: "12px", fontWeight: 600, color: "#64748b", marginBottom: "6px" }}>Closing Cash - JMD (counted)</label>
                        <InputNumber
                            value={closingCashJmd}
                            onValueChange={(e) => setClosingCashJmd(e.value)}
                            mode="currency"
                            currency="JMD"
                            locale="en-US"
                            min={0}
                            placeholder="0.00"
                            style={{ width: "100%" }}
                            inputStyle={{ width: "100%" }}
                        />
                    </div>
                    <Button label={busy ? "Clocking out..." : "Confirm & Clock Out"} icon={busy ? "pi pi-spin pi-spinner" : "pi pi-sign-out"} className={styles.dangerButton} onClick={handleConfirmClosingCash} disabled={busy} />
                </div>
            </Dialog>

            <Dialog header="Release Register" visible={showReleaseCashDialog} style={{ width: "min(420px, 92vw)" }} onHide={() => setShowReleaseCashDialog(false)}>
                <div style={{ display: "flex", flexDirection: "column", gap: "14px", marginTop: "6px" }}>
                    <p style={{ fontSize: "13px", color: "#64748b", margin: 0 }}>Count the cash left in the register and enter it below. Whoever acquires this register next will start fresh with their own starting cash.</p>
                    <div>
                        <label style={{ display: "block", fontSize: "12px", fontWeight: 600, color: "#64748b", marginBottom: "6px" }}>Closing Cash - USD (counted)</label>
                        <InputNumber
                            value={releaseClosingCashUsd}
                            onValueChange={(e) => setReleaseClosingCashUsd(e.value)}
                            mode="currency"
                            currency="USD"
                            locale="en-US"
                            min={0}
                            placeholder="0.00"
                            style={{ width: "100%" }}
                            inputStyle={{ width: "100%" }}
                        />
                    </div>
                    <div>
                        <label style={{ display: "block", fontSize: "12px", fontWeight: 600, color: "#64748b", marginBottom: "6px" }}>Closing Cash - JMD (counted)</label>
                        <InputNumber
                            value={releaseClosingCashJmd}
                            onValueChange={(e) => setReleaseClosingCashJmd(e.value)}
                            mode="currency"
                            currency="JMD"
                            locale="en-US"
                            min={0}
                            placeholder="0.00"
                            style={{ width: "100%" }}
                            inputStyle={{ width: "100%" }}
                        />
                    </div>
                    <Button label={registerBusy ? "Releasing..." : "Confirm & Release"} icon={registerBusy ? "pi pi-spin pi-spinner" : "pi pi-sign-out"} className={styles.dangerButton} onClick={handleConfirmReleaseCash} disabled={registerBusy} />
                </div>
            </Dialog>
        </div>
    );
};

export default ClockInOut;
